import React, { useState, useRef, useEffect } from "react";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import {
  User,
  Lock,
  FileText,
  Shield,
  HelpCircle,
  Camera,
  Eye,
  EyeOff,
  Mail,
  Plus,
  Minus,
  Edit2,
  Trash2,
  X,
} from "lucide-react";
import { useChangePasswordMutation } from "../store/api/adminAuthApi";
import {
  useGetFaqsQuery,
  useCreateFaqMutation,
  useUpdateFaqMutation,
  useDeleteFaqMutation,
  useGetPageQuery,
  useUpdatePageMutation,
} from "../store/api/adminApi";
import { useToast } from "../components/Toast";

/* ─── React 19–safe Quill wrapper ─── */
function QuillEditor({ value, onChange }) {
  const containerRef = useRef(null);
  const quillRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (quillRef.current) return;
    const container = containerRef.current;
    const el = document.createElement("div");
    container.appendChild(el);
    const quill = new Quill(el, {
      theme: "snow",
      modules: {
        toolbar: [
          [{ header: [1, 2, 3, false] }],
          ["bold", "italic", "underline", "strike"],
          [{ color: [] }, { background: [] }],
          ["link", "image"],
          [{ align: [] }],
          [{ list: "ordered" }, { list: "bullet" }],
          [{ indent: "-1" }, { indent: "+1" }],
          ["clean"],
        ],
      },
    });
    quill.root.innerHTML = value || "";
    quill.on("text-change", () => onChangeRef.current(quill.root.innerHTML));
    quillRef.current = quill;
    return () => {
      quill.off("text-change");
      quillRef.current = null;
      container.innerHTML = "";
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return <div ref={containerRef} className="settings-quill" />;
}

/* ─── Shared header ─── */
function PageHeader() {
  return (
    <header className="flex items-center justify-between px-8 pt-7 pb-4">
      <div>
        <h1 className="text-[22px] font-manrope-bold text-gray-900">
          Settings
        </h1>
        <p className="text-[12px] font-manrope-regular text-gray-400 mt-0.5">
          Manage all of the settings of your platform from here.
        </p>
      </div>
      <div className="flex items-center gap-4">
        <div className="w-9 h-9 rounded-full bg-white border border-gray-100 flex items-center justify-center shadow-sm">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            className="text-gray-600"
          >
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center">
            <span className="text-sm font-manrope-bold text-gray-600">V</span>
          </div>
          <div className="flex flex-col">
            <span className="text-[13px] font-manrope-semibold text-gray-900 leading-tight">
              Vigelanto
            </span>
            <span className="text-[11px] font-manrope-regular text-gray-400 leading-tight">
              muhammadmridulhosenkibria@gmail.com
            </span>
          </div>
        </div>
      </div>
    </header>
  );
}

/* ─── Delete FAQ modal ─── */
function DeleteFaqModal({ onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onCancel}
      />
      <div className="relative bg-white rounded-2xl p-8 w-[340px] flex flex-col items-center text-center shadow-2xl">
        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
          <Trash2 size={28} className="text-primary" strokeWidth={1.8} />
        </div>
        <h3 className="text-[16px] font-manrope-bold text-primary mb-2">
          Delete FAQ ?
        </h3>
        <p className="text-[12px] font-manrope-regular text-gray-400 mb-6 leading-relaxed">
          You are going to delete this FAQ. After deleting, this FAQ will no
          longer be available in your platform.
        </p>
        <div className="flex gap-3 w-full">
          <button
            onClick={onCancel}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 text-[13px] font-manrope-semibold text-gray-600 hover:bg-gray-50 transition-all"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 py-2.5 rounded-xl bg-primary text-white text-[13px] font-manrope-semibold hover:opacity-90 transition-all"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Add / Edit FAQ modal ─── */
function FaqModal({ mode = "add", faq = null, onClose, onSave, loading }) {
  const isEdit = mode === "edit";
  const [question, setQuestion] = useState(isEdit ? faq.question : "");
  const [answer, setAnswer] = useState(isEdit ? faq.answer : "");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-2xl shadow-2xl w-[440px] p-6 flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h2 className="text-[16px] font-manrope-bold text-gray-900">
            {isEdit ? "Edit FAQ" : "Add FAQ"}
          </h2>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center transition-all"
          >
            <X size={15} className="text-gray-500" />
          </button>
        </div>
        <div>
          <label className="block text-[13px] font-manrope-semibold text-gray-700 mb-1.5">
            Question
          </label>
          <input
            type="text"
            placeholder="Question goes here"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-[13px] font-manrope-regular text-gray-700 placeholder-gray-400 focus:outline-none focus:border-primary transition-all"
          />
        </div>
        <div>
          <label className="block text-[13px] font-manrope-semibold text-gray-700 mb-1.5">
            Answer
          </label>
          <textarea
            placeholder="Enter answer..."
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            rows={7}
            className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl text-[13px] font-manrope-regular text-gray-600 placeholder-gray-400 focus:outline-none focus:border-primary transition-all resize-none"
          />
        </div>
        <button
          disabled={loading || !question.trim() || !answer.trim()}
          onClick={() => onSave({ question, answer })}
          className="w-full py-3 bg-primary rounded-xl text-white text-[14px] font-manrope-semibold hover:opacity-90 transition-all disabled:opacity-60"
        >
          {loading ? "Saving…" : isEdit ? "Save changes" : "Add"}
        </button>
      </div>
    </div>
  );
}

/* ─── ACCOUNT section ─── */
function AccountSection() {
  const [name, setName] = useState("Admin");
  const [email, setEmail] = useState("admin@gmail.com");
  const [avatar, setAvatar] = useState(null);
  const fileRef = useRef();

  return (
    <div>
      <h2 className="text-[17px] font-manrope-bold text-gray-900 mb-6">
        Account settings
      </h2>
      <div className="flex justify-center mb-6">
        <div className="relative">
          <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
            {avatar ? (
              <img
                src={avatar}
                alt="avatar"
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-2xl font-manrope-bold text-gray-500">
                A
              </span>
            )}
          </div>
          <button
            onClick={() => fileRef.current.click()}
            className="absolute bottom-0 right-0 w-7 h-7 bg-primary rounded-full flex items-center justify-center shadow-md hover:opacity-90 transition-all"
          >
            <Camera size={13} className="text-white" strokeWidth={2} />
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files[0];
              if (f) setAvatar(URL.createObjectURL(f));
            }}
          />
        </div>
      </div>

      <div className="flex flex-col gap-3 mb-6">
        <div className="relative">
          <User
            size={15}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
            strokeWidth={1.6}
          />
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-[13px] font-manrope-regular text-gray-700 focus:outline-none focus:border-primary transition-all"
          />
        </div>
        <div className="relative">
          <Mail
            size={15}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
            strokeWidth={1.6}
          />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-[13px] font-manrope-regular text-gray-700 focus:outline-none focus:border-primary transition-all"
          />
        </div>
      </div>
      <div className="flex justify-end">
        <button className="px-6 py-2.5 bg-primary text-white text-[13px] font-manrope-semibold rounded-xl hover:opacity-90 transition-all">
          Save changes
        </button>
      </div>
    </div>
  );
}

/* ─── CHANGE PASSWORD section ─── */
function PasswordField({ label, value, onChange }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <div className="relative">
        <Lock
          size={15}
          className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
          strokeWidth={1.6}
        />
        <input
          type={show ? "text" : "password"}
          placeholder={label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full pl-10 pr-10 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-[13px] font-manrope-regular text-gray-700 placeholder-gray-400 focus:outline-none focus:border-primary transition-all"
        />
        <button
          onClick={() => setShow((s) => !s)}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-all"
        >
          {show ? (
            <EyeOff size={15} strokeWidth={1.6} />
          ) : (
            <Eye size={15} strokeWidth={1.6} />
          )}
        </button>
      </div>
    </div>
  );
}

function ChangePasswordSection() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const toast = useToast();

  const [changePassword, { isLoading }] = useChangePasswordMutation();

  const handleSave = async () => {
    setError("");
    if (next.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }
    if (next !== confirm) {
      setError("New passwords do not match.");
      return;
    }
    try {
      await changePassword({
        currentPassword: current,
        newPassword: next,
      }).unwrap();
      toast.success("Password changed successfully.");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      toast.error(err?.data?.error?.message || "Failed to change password.");
    }
  };

  return (
    <div>
      <h2 className="text-[17px] font-manrope-bold text-gray-900 mb-6">
        Change password
      </h2>
      <div className="flex flex-col gap-3 mb-4">
        <PasswordField
          label="Current Password"
          value={current}
          onChange={setCurrent}
        />
        <PasswordField label="New Password" value={next} onChange={setNext} />
        <PasswordField
          label="Confirm New Password"
          value={confirm}
          onChange={setConfirm}
        />
      </div>
      {error && (
        <p className="text-[12px] text-red-500 font-manrope-regular mb-3">
          {error}
        </p>
      )}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={isLoading || !current || !next || !confirm}
          className="px-6 py-2.5 bg-primary text-white text-[13px] font-manrope-semibold rounded-xl hover:opacity-90 transition-all disabled:opacity-60"
        >
          {isLoading ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

/* ─── RICH TEXT section (Terms & Privacy) ─── */
function RichTextSection({ title, slug }) {
  const { data: pageRes, isLoading } = useGetPageQuery(slug);
  const [updatePage, { isLoading: isSaving }] = useUpdatePageMutation();
  const [content, setContent] = useState(null);
  const [editorKey, setEditorKey] = useState(0);
  const toast = useToast();

  const prevSlug = useRef(slug);
  useEffect(() => {
    if (prevSlug.current !== slug) {
      prevSlug.current = slug;
      setContent(null);
    }
  }, [slug]);

  useEffect(() => {
    if (!isLoading && content === null) {
      setContent(pageRes?.data?.body || "");
      setEditorKey((k) => k + 1);
    }
  }, [isLoading, pageRes]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    try {
      await updatePage({ slug, body: content || "" }).unwrap();
      toast.success("Saved successfully.");
    } catch {
      toast.error("Failed to save. Please try again.");
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-48" />
        <div className="h-64 bg-gray-200 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <h2 className="text-[17px] font-manrope-bold text-gray-900 mb-4">
        {title}
      </h2>
      <div className="flex-1 mb-4">
        <QuillEditor key={editorKey} value={content ?? ""} onChange={setContent} />
      </div>
      <div className="flex justify-end mt-14">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="px-6 py-2.5 bg-primary text-white text-[13px] font-manrope-semibold rounded-xl hover:opacity-90 transition-all disabled:opacity-60"
        >
          {isSaving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

/* ─── FAQ section ─── */
function FaqSection() {
  const { data: faqRes, isLoading } = useGetFaqsQuery();
  const [createFaq, { isLoading: isCreating }] = useCreateFaqMutation();
  const [updateFaq, { isLoading: isUpdating }] = useUpdateFaqMutation();
  const [deleteFaq] = useDeleteFaqMutation();

  const faqs = faqRes?.data || [];

  const [openIds, setOpenIds] = useState([]);
  const [modal, setModal] = useState(null); // "add" | "edit"
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const toast = useToast();

  const toggle = (id) =>
    setOpenIds((ids) =>
      ids.includes(id) ? ids.filter((i) => i !== id) : [...ids, id],
    );

  const handleAdd = async (data) => {
    try {
      await createFaq(data).unwrap();
      setModal(null);
      toast.success("FAQ added successfully.");
    } catch (err) {
      toast.error(err?.data?.error?.message || "Failed to add FAQ.");
    }
  };

  const handleEdit = async (data) => {
    try {
      await updateFaq({ id: editTarget.id, ...data }).unwrap();
      setModal(null);
      setEditTarget(null);
      toast.success("FAQ updated successfully.");
    } catch (err) {
      toast.error(err?.data?.error?.message || "Failed to update FAQ.");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteFaq(deleteTarget.id).unwrap();
      toast.success("FAQ deleted.");
    } catch (err) {
      toast.error(err?.data?.error?.message || "Failed to delete FAQ.");
    } finally {
      setDeleteTarget(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3 animate-pulse">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-12 bg-gray-100 rounded-xl" />
        ))}
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-[17px] font-manrope-bold text-gray-900 mb-5">
        Frequently Asked Questions (FAQ)
      </h2>
      <div className="flex flex-col gap-3">
        {faqs.map((faq) => {
          const isOpen = openIds.includes(faq.id);
          return (
            <div key={faq.id} className="bg-gray-50 rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3">
                <span className="text-[13px] font-manrope-semibold text-gray-800">
                  {faq.question}
                </span>
                <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                  <button
                    onClick={() => toggle(faq.id)}
                    className="w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-200 transition-all"
                  >
                    {isOpen ? (
                      <Minus
                        size={11}
                        className="text-gray-600"
                        strokeWidth={2}
                      />
                    ) : (
                      <Plus
                        size={11}
                        className="text-gray-600"
                        strokeWidth={2}
                      />
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setEditTarget(faq);
                      setModal("edit");
                    }}
                    className="w-6 h-6 rounded-md border border-gray-200 flex items-center justify-center hover:bg-gray-200 transition-all"
                  >
                    <Edit2
                      size={11}
                      className="text-gray-500"
                      strokeWidth={1.6}
                    />
                  </button>
                  <button
                    onClick={() => setDeleteTarget(faq)}
                    className="w-6 h-6 rounded-md bg-primary/10 flex items-center justify-center hover:bg-primary/20 transition-all"
                  >
                    <Trash2
                      size={11}
                      className="text-primary"
                      strokeWidth={1.6}
                    />
                  </button>
                </div>
              </div>
              {isOpen && (
                <div className="px-4 pb-4">
                  <div className="w-full h-px bg-gray-200 mb-3" />
                  <p className="text-[12px] font-manrope-regular text-gray-500 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <button
        onClick={() => setModal("add")}
        className="mt-4 flex items-center gap-2 px-5 py-2.5 bg-primary/10 text-primary text-[13px] font-manrope-semibold rounded-xl hover:bg-primary/20 transition-all"
      >
        <Plus size={14} strokeWidth={2.2} />
        Add more
      </button>

      {modal === "add" && (
        <FaqModal
          mode="add"
          loading={isCreating}
          onClose={() => setModal(null)}
          onSave={handleAdd}
        />
      )}
      {modal === "edit" && (
        <FaqModal
          mode="edit"
          faq={editTarget}
          loading={isUpdating}
          onClose={() => {
            setModal(null);
            setEditTarget(null);
          }}
          onSave={handleEdit}
        />
      )}
      {deleteTarget && (
        <DeleteFaqModal
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
        />
      )}
    </div>
  );
}

/* ─── Settings nav items ─── */
const NAV_ITEMS = [
  { key: "account", label: "Account", icon: User },
  { key: "password", label: "Change password", icon: Lock },
  { key: "terms", label: "Terms & Conditions", icon: FileText },
  { key: "privacy", label: "Privacy policy", icon: Shield },
  { key: "faq", label: "FAQ", icon: HelpCircle },
];

/* ─── Main Settings Page ─── */
export default function Settings() {
  const [active, setActive] = useState("account");

  const renderContent = () => {
    switch (active) {
      case "account":
        return <AccountSection />;
      case "password":
        return <ChangePasswordSection />;
      case "terms":
        return <RichTextSection title="Terms & Conditions" slug="terms" />;
      case "privacy":
        return <RichTextSection title="Privacy policy" slug="privacy" />;
      case "faq":
        return <FaqSection />;
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <PageHeader />
      <div className="flex-1 px-8 pb-8 flex gap-5">
        {/* Settings sidebar */}
        <div className="w-[200px] flex-shrink-0">
          <div className="bg-white rounded-2xl overflow-hidden">
            {NAV_ITEMS.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActive(key)}
                className={`w-full flex items-center gap-3 px-4 py-3.5 text-[13px] transition-all border-b border-gray-50 last:border-0 ${
                  active === key
                    ? "font-manrope-bold text-gray-900 bg-gray-50 border-l-[3px] border-l-gray-900"
                    : "font-manrope-regular text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                }`}
              >
                <Icon size={15} strokeWidth={active === key ? 2.2 : 1.6} />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Content area */}
        <div className="flex-1 bg-white rounded-2xl p-6 min-h-[400px]">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
